import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { GoogleMapsModule } from "@angular/google-maps";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import {
  NgbAlertModule,
  NgbDatepickerModule,
  NgbTimepickerModule,
} from "@ng-bootstrap/ng-bootstrap";
import { DateTime } from "luxon";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";
import { DatePickerModule } from "primeng/datepicker";
import { SelectModule } from "primeng/select";
import { EditorModule } from "primeng/editor";
import { FileUploadModule } from "primeng/fileupload";
import { InputTextModule } from "primeng/inputtext";
import { TextareaModule } from "primeng/textarea";
import { distinctUntilChanged, first, Subscription } from "rxjs";
import { GoogleAddress } from "../../../../app/interfaces/cep";
import { BlobStorageService } from "../../../../app/services/blob-storage.service";
import { EventsManagementService } from "../../../../app/services/events-management.service";
import { GoogleMapsLoaderService } from "../../../../app/services/google-maps-loader.service";
import { GoogleMapsService } from "../../../../app/services/google-maps.service";
import { LoadingService } from "../../../../app/services/loading.service";
import { LocalStorageService } from "../../../../app/services/local-storage.service";
import { SearchPostalCodeService } from "../../../../app/services/search-postal-code.service";
import { SnackBarService } from "../../../../app/services/snack-bar.service";
import { TicketManagementService } from "../../../../app/services/ticket-management.service";
import { EventInfoRequestBody } from "../../../../app/services/types/events-management-request.type";
import {
  EventInfoResponseBody,
  ICreateEventResponse,
} from "../../../../app/services/types/events-management-response.type";
import { defaultEventInfoResponseBody } from "../../../../app/services/utils/events-management-response.default-values";
import { environment } from "../../../../environments/environment";
import { EventLocationState } from "../../../../utils/constants/event-location-state-brazil.constant";
import { sortBatchesByStartDate } from "../../../../utils/utils";
import { BatchSummaryComponent } from "../../batch-summary/batch-summary.component";
import { ButtonComponent } from "../../button/button.component";
import {
  ModalTicketsComponent,
  ModalTicketsDialogData,
} from "../../modal-tickets/modal-tickets.component";
import { TicketBatch } from "../../modal-tickets/types/ticket-batch.type";
import { PageHeaderComponent } from "../../page-header/page-header.component";
import { TicketTableComponent } from "../../ticket-table/ticket-table.component";
import { eventHasPaidSales } from "../../../helpers/ticket-sales-lock.helper";
import { EventCategory } from "./../../../../app/services/types/events-management-response.type";
import { EventDate } from "./types/event-date.type";

@Component({
  selector: "app-card-manage-event",
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbDatepickerModule,
    NgbTimepickerModule,
    NgbAlertModule,
    EditorModule,
    GoogleMapsModule,
    TicketTableComponent,
    InputTextModule,
    SelectModule,
    TextareaModule,
    FileUploadModule,
    DatePickerModule,
    ButtonComponent,
    NgxMaskDirective,
    BatchSummaryComponent,
    PageHeaderComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: "./card-manage-event.component.html",
  styleUrl: "./card-manage-event.component.scss",
})
export class CardManageEventComponent implements OnInit, OnDestroy {
  constructor(
    private readonly router: Router,
    private localStorageService: LocalStorageService,
    private loadingService: LoadingService,
    @Optional()
    public readonly dialogRef?: MatDialogRef<CardManageEventComponent>,
  ) {}

  @Output() currentStepEvent = new EventEmitter<number>();
  @Input() updateEvent = false;
  eventCategories: EventCategory[] = [];
  eventLocationState = EventLocationState;
  currentStep = 1;
  formGroupStep1!: FormGroup;
  formGroupStep2!: FormGroup;
  formGroupStep3!: FormGroup;
  uploadedImage: string | ArrayBuffer | null = null;
  uploadedMapImage: string | ArrayBuffer | null = null;
  center: google.maps.LatLngLiteral = { lat: -23.55052, lng: -46.633308 };
  isMapLoaded = false;
  ticketBatches: TicketBatch[] = [];
  originalTicketBatches: TicketBatch[] = [];

  get transitionDisabledReason(): string {
    return JSON.stringify(this.ticketBatches) !==
      JSON.stringify(this.originalTicketBatches)
      ? "Salve as alterações dos lotes antes de fazer a virada."
      : "";
  }

  onBatchTransitioned(batches: TicketBatch[]): void {
    if (!this.updateEvent || !this.eventInfo?.id) return;
    this.ticketBatches = batches;
    this.originalTicketBatches = JSON.parse(JSON.stringify(batches));
  }
  private localImageFileBuffer: File | null = null;
  private localMapImageFileBuffer: File | null = null;
  private currentUser = JSON.parse(
    this.localStorageService.getItem("current_user") ?? undefined,
  );

  private sasToken = environment.SAS_TOKEN_BLOB_STORAGE_AZURE;
  private imageExists = false;
  private bannerFile: File | null = null;
  private filePath = "";
  private filePathMap = "";
  eventInfo: EventInfoResponseBody = defaultEventInfoResponseBody;
  private formGroupStep2PostalSub$: Subscription | undefined;
  private formGroupStep2AddressChangeSub$: Subscription | undefined;
  private readonly formBuilder = inject(FormBuilder);
  private readonly googleMapService = inject(GoogleMapsService);
  private readonly mapsLoader = inject(GoogleMapsLoaderService);
  private readonly dialog = inject(MatDialog);
  private readonly searchPostalCodeService = inject(SearchPostalCodeService);
  private readonly eventsManagementService = inject(EventsManagementService);
  private readonly ticketsManagementService = inject(TicketManagementService);
  private readonly snackbarService = inject(SnackBarService);
  private readonly blobService = inject(BlobStorageService);

  submitErrors: string[] = [];

  loading = false;
  addressSugestions: GoogleAddress[] = [];
  showSugestions = false;
  paidEvent = true;
  showImageInput = false;
  showMapImageInput = false;

  onClose() {
    if (this.dialogRef) this.dialogRef.close();
  }

  loadEventCategories() {
    this.eventsManagementService.getEventsCategories().subscribe({
      next: (response) => {
        if (response.length > 0) {
          this.eventCategories = response;
        }
      },
      error: (error) => {
        console.error("Erro ao carregar o eventos:", error);
      },
    });
  }

  ngOnInit() {
    if (!this.updateEvent) {
      this.showImageInput = true;
      this.showMapImageInput = true;
    }

    this.loading = true;
    // try {
    this.initializeFormGroups();
    this.subscribeToFormChanges();
    this.loadGoogleMaps();
    this.loadEventImage();
    this.loadEventCategories();
    // } catch (error) {
    //   console.log("Erro ao inicializar o componente", error);
    // } finally {
    //   this.loading = false;
    // }
  }

  ngOnDestroy() {
    this.formGroupStep2PostalSub$?.unsubscribe();
    this.formGroupStep2AddressChangeSub$?.unsubscribe();
  }

  nextStep() {
    const currentFormGroup = this.getCurrentFormGroup();

    if (currentFormGroup?.valid) {
      this.currentStep++;
      this.currentStepEvent.emit(this.currentStep);
    } else if (this.currentStep++ === 4) {
      this.currentStepEvent.emit(this.currentStep);
    } else {
      currentFormGroup?.markAllAsTouched();
    }
  }

  editTicket = (batchIndex: number, ticketIndex: number) => {
    this.openModalTickets(batchIndex, ticketIndex);
  };

  openModalTickets = (
    batchIndex?: number,
    ticketIndex?: number,
    editBatch = false,
  ) => {
    const eventDate: EventDate = {
      startDate: this.formGroupStep1.value.eventDateGroup.start_date,
      endDate: this.formGroupStep1.value.eventDateGroup.end_date,
      startTime: this.formGroupStep1.value.eventDateGroup.start_time,
      endTime: this.formGroupStep1.value.eventDateGroup.end_time,
    };
    const dialogRef = this.dialog.open(ModalTicketsComponent, {
      width: "800px",
      maxWidth: "95vw",
      data: {
        isTicketFree: !this.paidEvent,
        eventDate: eventDate,
        ticketBatches: this.ticketBatches,
        selectedBatch: batchIndex,
        selectedTicket: ticketIndex,
        editBatch: editBatch,
      } as ModalTicketsDialogData,
    });

    dialogRef.componentInstance.ticketsEvent.subscribe(
      (ticketBatches: TicketBatch[]) => {
        this.handleTicketBatchChange(ticketBatches);
      },
    );
  };

  handleTicketBatchChange(ticketBatchEvent: TicketBatch[]) {
    this.ticketBatches = ticketBatchEvent;
  }

  handleChangeEventType(isPaidEvent: boolean) {
    if (this.paidEvent === isPaidEvent) {
      return;
    }

    if (this.eventInfo?.id && eventHasPaidSales(this.ticketBatches)) {
      alert(
        "Não é possível alterar o tipo de evento após o início das vendas.",
      );
      return;
    }

    if (this.ticketBatches.length > 0) {
      const response = confirm(
        isPaidEvent
          ? "Os ingressos existentes serão mantidos. Depois você poderá definir o valor de cada um. Deseja continuar?"
          : "Os ingressos existentes serão mantidos e os valores serão zerados. Deseja continuar?",
      );

      if (!response) return;

      if (!isPaidEvent) {
        this.ticketBatches = this.ticketBatches.map((batch) => ({
          ...batch,
          tickets: (batch.tickets ?? []).map((ticket) => ({
            ...ticket,
            price: 0,
          })),
        }));
      }
    }

    this.paidEvent = isPaidEvent;
  }

  onFileChange(event: Event) {
    if (
      event.target instanceof HTMLInputElement &&
      event.target.files?.length
    ) {
      const [file] = event.target.files;
      const reader = new FileReader();
      this.localImageFileBuffer = file;

      if (!this.currentUser?.id) {
        console.error("Usuário não autenticado ou e-mail não encontrado");
        return;
      }

      reader.readAsDataURL(this.localImageFileBuffer);
      reader.onload = () => {
        // Define o caminho do "diretório" com o e-mail do usuário (simula a pasta)
        this.filePath = `banner-${this.currentUser.id}/${file.name}`;

        this.uploadedImage = reader.result;
        this.formGroupStep3.patchValue({
          uploadBanner: this.localImageFileBuffer,
        });
      };
    }
  }

  onMapFileChange(event: Event) {
    if (
      event.target instanceof HTMLInputElement &&
      event.target.files?.length
    ) {
      const [file] = event.target.files;
      const reader = new FileReader();
      this.localMapImageFileBuffer = file;

      if (!this.currentUser?.id) {
        console.error("Usuário não autenticado ou e-mail não encontrado");
        return;
      }

      reader.readAsDataURL(this.localMapImageFileBuffer);
      reader.onload = () => {
        // Define o caminho do "diretório" com o e-mail do usuário (simula a pasta)
        this.filePathMap = `map-${this.currentUser.id}/${file.name}`;

        this.uploadedMapImage = reader.result;
        this.formGroupStep3.patchValue({
          mapaEvento: this.localMapImageFileBuffer,
        });
      };
    }
  }

  onImageLoad(event: Event) {
    const img = event.target as HTMLImageElement;
    const maxWidth = 500;
    const maxHeight = 400;

    if (img.width > maxWidth || img.height > maxHeight) {
      const aspectRatio = img.width / img.height;

      if (aspectRatio > maxWidth / maxHeight) {
        img.width = maxWidth;
        img.height = maxWidth / aspectRatio;
      } else {
        img.height = maxHeight;
        img.width = maxHeight * aspectRatio;
      }
    }
  }

  onAddressChange() {
    const address = this.formatAddressMaps();
    if (address.length <= 4) {
      return;
    }

    const currentMapsUrl = this.formGroupStep2.get("maps_url")?.value;

    if (currentMapsUrl) {
      const url = new URL(currentMapsUrl);
      const query = url.searchParams.get("query");

      if (query) {
        const [lat, lng] = query.split(",").map((coord) => parseFloat(coord));

        if (!isNaN(lat) && !isNaN(lng)) {
          // Atualiza o centro do mapa com as coordenadas extraídas
          this.center = { lat, lng };
        }
      }
    }

    this.googleMapService
      .getGeoCode(address)
      .pipe(first())
      .subscribe((response) => {
        if (response.results.length > 0) {
          const location = response.results[0].geometry.location;
          const newMapsUrl = this.googleMapService.getMapsUrl(
            location.lat,
            location.lng,
          );

          // Compare a nova URL com a atual antes de atualizar o formulário
          if (currentMapsUrl !== newMapsUrl) {
            this.center = { lat: location.lat, lng: location.lng };
            this.addressSugestions = response.results;
            this.formGroupStep2.get("maps_url")?.setValue(newMapsUrl);
          }
        } else {
          this.addressSugestions = [];
        }
      });
  }

  setSugestionAsAddress(sugestion: GoogleAddress) {
    let street = "";
    let neighborhood = "";
    let city = "";
    let state = "";
    let zip = "";

    sugestion.address_components.forEach((component) => {
      if (component.types.includes("route")) {
        street = component.short_name;
      }
      if (
        component.types.includes("sublocality") ||
        component.types.includes("neighborhood")
      ) {
        neighborhood = component.short_name;
      }
      if (component.types.includes("administrative_area_level_2")) {
        city = component.short_name;
      }
      if (component.types.includes("administrative_area_level_1")) {
        state = component.short_name;
      }
      if (component.types.includes("postal_code")) {
        zip = component.short_name;
      }
    });

    this.formGroupStep2.patchValue({
      street: street,
      neighborhood: neighborhood,
      city: city,
      state: state,
      zip: zip,
    });

    this.showSugestions = false;
  }

  formatAddressMaps(): string {
    const { location_name, street, number, city, state } =
      this.formGroupStep2.value;

    const addressParts = [location_name, street, number, city, state].map(
      (part) => String(part ?? "").trim(),
    );

    return addressParts.filter(Boolean).join(" ");
  }

  formatAddressView() {
    const { location_name, street, number, neighborhood, city, state } =
      this.formGroupStep2.value;

    const formattedAddress = [
      location_name,
      street && number ? `${street}, ${number}` : street,
      neighborhood,
      `${city} - ${state}`,
    ]
      .filter(Boolean) // Remove elementos vazios ou nulos
      .join(" - "); // Separador visual para maior clareza

    return formattedAddress;
  }

  clearAddress() {
    this.formGroupStep2.patchValue({
      street: "",
      neighborhood: "",
      city: "",
      state: "",
      zip: "",
    });
    this.showSugestions = false;
    this.addressSugestions = [];
  }

  getCurrentFormGroup(): FormGroup | null {
    switch (this.currentStep) {
      case 1:
        return this.formGroupStep1;
      case 2:
        return this.formGroupStep2;
      case 3:
        return this.formGroupStep3;
      default:
        return null;
    }
  }

  submitForm() {
    this.submitErrors = [];
    this.loadingService.show();

    try {
      if (
        this.formGroupStep1.valid &&
        this.formGroupStep2.valid &&
        this.formGroupStep3.valid
      ) {
        const {
          title,
          status,
          is_private,
          event_category_id,
          description,
          eventDateGroup,
          accepted_terms,
        } = this.formGroupStep1.value;

        const {
          city,
          location_name,
          state,
          neighborhood,
          number,
          street,
          zip,
          maps_url,
        } = this.formGroupStep2.value;

        let image_url = this.eventInfo.thumbnail
          ? this.eventInfo.thumbnail
          : "";

        let additional_images = this.eventInfo.additional_images
          ? this.eventInfo.additional_images
          : [];

        if (this.localImageFileBuffer) {
          image_url = this.sendBannerImageToBlobStorage();
        }

        if (this.localMapImageFileBuffer) {
          additional_images = [this.sendMapImageToBlobStorage()];
        }

        const fullStartDate = DateTime.fromISO(
          `${eventDateGroup.start_date}T${eventDateGroup.start_time}`,
        ).toISO();

        const fullEndDate = DateTime.fromISO(
          `${eventDateGroup.end_date}T${eventDateGroup.end_time}`,
        ).toISO();

        const newEventInfo: EventInfoRequestBody = {
          event: {
            online: false,
            platform_url: "",
            title: title,
            description: description,
            thumbnail: image_url,
            additional_images: additional_images,
            start_date: fullStartDate!,
            end_date: fullEndDate!,
            accepted_terms: accepted_terms,
            is_private: is_private,
            status: status,
            event_category_id: event_category_id,
            address_attributes: {
              location_name: location_name,
              city_name: city,
              state: state,
              country: this.eventInfo.address?.country ?? "BR",
              district: neighborhood,
              street: street,
              postal_code: zip,
              number: number,
              additional_information: "FALTA IMPLEMENTAR",
              maps_url: maps_url,
            },
          },
        };

        console.log("EVENT INFO", newEventInfo);

        if (this.updateEvent) {
          this.sendUpdateEventRequest(newEventInfo);
        } else {
          this.sendCreateEventRequest(newEventInfo);
        }
      } else {
        this.snackbarService.openSnackBar(
          "Formulário não tem dados validos",
          "Fechar",
          "error",
        );
      }
    } catch (error: any) {
      console.log("Erro ao atualizar evento", error);
      this.handleErrorMessage(error, "Erro ao atualizar evento:");
    } finally {
      this.loadingService.hide();
    }
  }

  handleErrorMessage(error: any, additionalErrorMessage: string) {
    if (error?.error?.errors?.length > 0) {
      error?.error?.errors?.map((errorMessage: string) => {
        const errorTranslated = this.translateFormErrors(errorMessage);

        this.submitErrors.push(`${additionalErrorMessage}: ${errorTranslated}`);
        this.snackbarService.openSnackBar(errorTranslated, "Fechar", "error");
      });
    } else if (error?.error?.error) {
      const errorTranslated = this.translateFormErrors(error.error.error);

      this.submitErrors.push(`${additionalErrorMessage}: ${errorTranslated}`);
      this.snackbarService.openSnackBar(errorTranslated, "Fechar", "error");
    } else {
      this.snackbarService.openSnackBar(
        additionalErrorMessage,
        "Fechar",
        "error",
      );
    }
    return;
  }

  getEventChangedFields = (newFormValues: any) => {
    let oldEventInfoForm: any = this.eventInfo;

    oldEventInfoForm.address_attributes = oldEventInfoForm.address;

    const changedFields: Record<string, any> = {};

    // Função recursiva para comparar objetos aninhados
    const compareValues = (
      initialValue: any,
      currentValue: any,
      path: string = "",
    ) => {
      // Se é um objeto (mas não um array ou null)
      if (
        initialValue &&
        currentValue &&
        typeof initialValue === "object" &&
        typeof currentValue === "object" &&
        !Array.isArray(initialValue) &&
        !Array.isArray(currentValue)
      ) {
        // Iterar sobre as chaves do objeto atual
        Object.keys(currentValue).forEach((key) => {
          const newPath = path ? `${path}.${key}` : key;
          compareValues(initialValue[key], currentValue[key], newPath);
        });
      }
      // Se os valores são diferentes, adiciona ao objeto de campos alterados
      else if (initialValue !== currentValue) {
        if (path) {
          // Constrói a estrutura de objeto aninhado para o caminho
          const pathParts = path.split(".");
          let current = changedFields;

          pathParts.forEach((part, index) => {
            if (index === pathParts.length - 1) {
              current[part] = currentValue;
            } else {
              if (!current[part]) current[part] = {};
              current = current[part];
            }
          });
        }
      }
    };

    // Comparar os valores iniciais com os valores atuais
    compareValues(oldEventInfoForm, newFormValues);

    return changedFields;
  };

  private sendUpdateEventRequest(eventInfo: EventInfoRequestBody) {
    const eventChangedFields = {
      event: this.getEventChangedFields(eventInfo.event),
    };

    if (Object.keys(eventChangedFields.event).length > 0) {
      this.eventsManagementService
        .updateEvent(Number(this.eventInfo.id), eventChangedFields as any)
        .pipe(first())
        .subscribe({
          next: (response) => {
            // this.snackbarService.openSnackBar(
            //   "Evento atualizado com sucesso!",
            //   "Fechar",
            //   "success",
            // );
          },
          error: (error: any) => {
            this.handleErrorMessage(error, `Erro ao atualizar o evento`);
          },
        });
    }

    this.updateEventBatches();
  }

  private sendCreateEventRequest(eventInfo: EventInfoRequestBody) {
    this.eventsManagementService
      .createEvent(eventInfo)
      .pipe(first())
      .subscribe({
        next: (response: ICreateEventResponse) => {
          this.linkTicketsToCreatedEvent(response);
          this.snackbarService.openSnackBar(
            "Evento criado com sucesso!",
            "Fechar",
            "success",
          );
          this.router.navigate([
            response?.event_id ? `/event/${response.event_id}` : "/my-events",
          ]);
        },
        error: (error: Error) => {
          this.handleErrorMessage(error, `Erro ao criar o evento`);
        },
      });
  }

  private loadEventBatches() {
    if (this.eventInfo.id) {
      this.eventsManagementService
        // @ts-ignore
        .getEventBatches(this.eventInfo.id)
        .subscribe({
          next: (eventBatches) => {
            if (eventBatches.length > 0) {
              const resultBatches = sortBatchesByStartDate(
                eventBatches.map((batch) => {
                  if (batch.tickets.some((ticket) => ticket.price > 0)) {
                    this.paidEvent = true;
                  } else {
                    this.paidEvent = false;
                  }
                  return batch;
                }),
              );

              if (resultBatches.length > 0) {
                // @ts-ignore
                this.ticketBatches = resultBatches;
                this.originalTicketBatches = JSON.parse(
                  JSON.stringify(resultBatches),
                );

                if (
                  this.ticketBatches.some((batch) =>
                    batch.tickets.some((ticket) => ticket.price > 0),
                  )
                ) {
                  this.paidEvent = true;
                } else {
                  this.paidEvent = false;
                }
              }
            }
          },
          error: (error) => {
            console.error("Erro ao consultar lotes do evento:", error);
          },
        });
    }
  }

  private createTicketBatch(eventId: number, ticketBatch: TicketBatch) {
    this.ticketsManagementService
      .createTicketBatch(eventId, ticketBatch)
      .pipe(first())
      .subscribe({
        next: (_) => {
          this.snackbarService.openSnackBar(
            "Ingresso criado com sucesso!",
            "Fechar",
            "success",
          );
        },
        error: (error: Error) => {
          this.handleErrorMessage(error, "Erro ao criar novo lote:");
        },
      });
  }

  private linkTicketsToCreatedEvent(response: ICreateEventResponse) {
    for (const ticketBatch of this.ticketBatches) {
      this.createTicketBatch(Number(response.event_id), ticketBatch);
    }
  }

  translateFormErrors(error: string) {
    switch (error) {
      case "Sale end must be before the previous batch's sale_start for this event.":
        return "A data de término da venda precisa ser anterior à data de início da próxima venda para este evento.";
      case "Sale start must be after the previous batch's sale_end for this event.":
        return "A data de início da venda precisa ser posterior à data de término da venda anterior para este evento.";
      case "Sale end must be after the sale_start.":
        return "A data de término deve ser antes da data de início.";
      default:
        return error;
    }
  }

  private updateEventBatches() {
    this.loadingService.show();
    try {
      for (const ticketBatch of this.ticketBatches) {
        const originalBatch = this.originalTicketBatches.find(
          (item) => item.id == ticketBatch.id,
        );

        // Update batch start e end date
        if (ticketBatch.id) {
          if (
            (originalBatch?.sale_start
              ? Date.parse(originalBatch.sale_start)
              : null) !==
              (ticketBatch.sale_start
                ? Date.parse(ticketBatch.sale_start)
                : null) ||
            (originalBatch?.sale_end
              ? Date.parse(originalBatch.sale_end)
              : null) !==
              (ticketBatch.sale_end
                ? Date.parse(ticketBatch.sale_end)
                : null) ||
            (originalBatch?.transition_mode ?? "date_or_sold_out") !==
              (ticketBatch.transition_mode ?? "date_or_sold_out") ||
            !!originalBatch?.absorbs_platform_fee !==
              !!ticketBatch.absorbs_platform_fee ||
            !!originalBatch?.absorbs_processing_fee !==
              !!ticketBatch.absorbs_processing_fee ||
            !!originalBatch?.sold_at_pdv !== !!ticketBatch.sold_at_pdv
          ) {
            const requestUpdateBatch = {
              sale_start: ticketBatch.sale_start,
              sale_end: ticketBatch.sale_end,
              transition_mode:
                ticketBatch.transition_mode ?? "date_or_sold_out",
              absorbs_platform_fee: !!ticketBatch.absorbs_platform_fee,
              absorbs_processing_fee: !!ticketBatch.absorbs_processing_fee,
              sold_at_pdv: !!ticketBatch.sold_at_pdv,
            };

            this.ticketsManagementService
              .updateBatch(Number(ticketBatch.id), requestUpdateBatch)
              .pipe(first())
              .subscribe({
                next: (response) => {
                  ticketBatch.sale_start = response.sale_start;
                  ticketBatch.sale_end = response.sale_end;
                  ticketBatch.transition_mode = response.transition_mode;
                  ticketBatch.sequence_number = response.sequence_number;
                  ticketBatch.transition_closed_at =
                    response.transition_closed_at;
                  console.log(
                    `RESPONSE UPDATE BATCH ${ticketBatch.id}`,
                    response,
                  );
                },
                error: (error: any) => {
                  console.log("ERROR", error.error);
                  this.handleErrorMessage(
                    error,
                    `Erro ao atualizar lote #${ticketBatch.id}`,
                  );
                },
              });
          }
        }
        // Cria novo batch
        else {
          this.createTicketBatch(Number(this.eventInfo.id), ticketBatch);
        }

        // Update tickets info
        for (const ticket of ticketBatch.tickets) {
          if (ticket.id) {
            const originalTicketInfo = originalBatch?.tickets.find(
              (item) => item.id == ticket.id,
            );
            console.log(
              "TICKET changed?????",
              ticket.id,
              JSON.stringify(originalTicketInfo) !== JSON.stringify(ticket),
            );

            if (JSON.stringify(originalTicketInfo) !== JSON.stringify(ticket)) {
              this.ticketsManagementService
                .updateTicketInBatch(Number(ticketBatch.id), ticket.id, ticket)
                .pipe(first())
                .subscribe({
                  next: (response) => {
                    console.log(
                      `RESPONSE TICKET ${ticket.id} BATCH ${ticketBatch.id} `,
                      response,
                    );
                  },
                  error: (error: any) => {
                    console.log("ERRO ao atualizar ingresso", error);
                    this.handleErrorMessage(
                      error,
                      `Erro ao atualizar ingresso #${ticket.id}`,
                    );
                  },
                });
            }
          } else {
            if (ticketBatch.id) {
              this.ticketsManagementService
                .createTicketInBatch(Number(ticketBatch.id), ticket)
                .pipe(first())
                .subscribe({
                  next: (response) => {
                    console.log(
                      `RESPONSE create TICKET ${ticket.id} BATCH ${ticketBatch.id} `,
                      response,
                    );
                  },
                  error: (error: any) => {
                    this.handleErrorMessage(
                      error,
                      `Erro ao criar ingresso no lote #${ticketBatch.id}`,
                    );
                  },
                });
            } else {
              // this.createTicketBatch(Number(this.eventInfo.id), ticketBatch);
            }
          }
        }
      }
    } catch (error) {
      console.log("Erro ao atualizar batches", error);
      this.handleErrorMessage(error, `Erro ao atualizar lotes`);
    } finally {
      this.loadingService.hide();

      if (this.submitErrors.length == 0) {
        this.onClose();

        this.snackbarService.openSnackBar(
          "Evento atualizado com sucesso!",
          "Fechar",
          "success",
        );
      }
    }
  }

  private initializeFormGroups() {
    const startDate = DateTime.fromISO(this.eventInfo.start_date);
    const endDate = DateTime.fromISO(this.eventInfo.end_date);

    const startTime = startDate.isValid ? startDate.toFormat("HH:mm") : "";
    const endTime = endDate.isValid ? endDate.toFormat("HH:mm") : "";

    this.formGroupStep1 = this.formBuilder.group({
      title: [this.eventInfo.title, Validators.required],
      status: [this.eventInfo.status, Validators.required],
      is_private: [this.eventInfo.is_private, Validators.required],
      accepted_terms: [this.eventInfo.accepted_terms, Validators.requiredTrue],
      event_category_id: [
        this.eventInfo.event_category.id,
        Validators.required,
      ],
      description: [this.eventInfo.description, Validators.required],
      eventDateGroup: this.formBuilder.group(
        {
          start_date: [this.eventInfo.start_date, Validators.required],
          end_date: [this.eventInfo.end_date, Validators.required],
          start_time: [startTime, Validators.required],
          end_time: [endTime, Validators.required],
        },
        // TODO: Verify this code here
        // {
        //   validators: this.updateEvent
        //     ? [eventDateRangeValidatorFormUpdate()]
        //     : [eventDateRangeValidatorFormCreate()],
        // },
      ),
    });
    this.formGroupStep2 = this.formBuilder.group({
      location_name: [
        this.eventInfo.address.location_name,
        Validators.required,
      ],
      zip: [this.eventInfo.address.postal_code, Validators.required],
      city: [this.eventInfo.address.city_name, Validators.required],
      state: [this.eventInfo.address.state, Validators.required],
      street: [this.eventInfo.address.street, Validators.required],
      number: [this.eventInfo.address.number, Validators.required],
      neighborhood: [this.eventInfo.address.district, Validators.required],
      maps_url: [this.eventInfo.address.maps_url, Validators.required],
    });
    this.formGroupStep3 = this.formBuilder.group({
      uploadBanner: ["", Validators.required],
      mapaEvento: [""],
    });

    this.onAddressChange();

    this.loadEventBatches();
  }

  private subscribeToFormChanges() {
    this.formGroupStep2AddressChangeSub$ = this.formGroupStep2.valueChanges
      .pipe(
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe(() => {
        this.onAddressChange();
      });
  }

  private loadGoogleMaps() {
    const apiKey = environment.GOOGLE_API_KEY_MAPS;

    this.mapsLoader
      .load(apiKey)
      .then(() => {
        this.isMapLoaded = true;
        this.onAddressChange();
      })
      .catch((err) => {
        console.error("Erro ao carregar o Google Maps", err);
      });
  }

  searchPostalCode() {
    const cep = this.formGroupStep2.value.zip.replace("-", "");

    if (cep.length < 8) {
      this.snackbarService.openSnackBar(
        "Por favor informe um CEP válido!",
        "Fechar",
        "error",
      );
      return;
    }

    this.loading = true;

    try {
      this.searchPostalCodeService
        .getPostalCode(cep)
        .pipe(first())
        .subscribe({
          next: (data) => {
            if (!data) {
              return;
            }
            this.formGroupStep2.patchValue({
              street: data.logradouro,
              city: data.localidade,
              state: data.uf,
              neighborhood: data.bairro,
              zip: cep,
            });
          },
          error: (error: Error) => {
            this.snackbarService.openSnackBar(
              "Erro ao buscar CEP, por favor informe um CEP válido!",
              "Fechar",
              "error",
            );
          },
        });
    } catch (error) {
      console.log("Erro ao consultar CEP");
    } finally {
      this.loading = false;
    }
  }

  private sendBannerImageToBlobStorage() {
    const image_url = this.filePath;

    this.blobService.uploadImage(
      this.sasToken,
      this.localImageFileBuffer!,
      image_url,
      () => {
        this.loadEventImage();
      },
    );

    return image_url;
  }

  private sendMapImageToBlobStorage() {
    const image_url = this.filePathMap;

    this.blobService.uploadImage(
      this.sasToken,
      this.localMapImageFileBuffer!,
      image_url,
      () => {
        this.loadEventImage();
      },
    );

    return image_url;
  }

  private deleteImage(name: string) {
    this.blobService.deleteImage(this.sasToken, name, () => {
      this.loadEventImage();
    });
  }

  private async loadEventImage() {
    if (
      this.eventInfo.thumbnail != "" &&
      this.eventInfo.thumbnail != "FALTA IMPLEMENTAR"
    ) {
      this.uploadedImage = `${environment.BLOB_URL}/${this.eventInfo.thumbnail}`;
      this.formGroupStep3.get("uploadBanner")?.clearValidators();
      this.formGroupStep3.get("uploadBanner")?.updateValueAndValidity();
    } else {
      this.showImageInput = true;
    }

    if (this.eventInfo.additional_images?.length > 0) {
      this.uploadedMapImage = `${environment.BLOB_URL}/${this.eventInfo.additional_images[0]}`;
      this.formGroupStep3.get("mapaEvento")?.clearValidators();
      this.formGroupStep3.get("mapaEvento")?.updateValueAndValidity();
    } else {
      this.showMapImageInput = true;
    }
  }

  getEventCategoryName(category_id: number) {
    const itemFound = this.eventCategories.find(
      (item) => item.id == category_id,
    );
    if (itemFound) {
      return itemFound.title;
    } else {
      return "";
    }
  }

  getFormErrors(form: FormGroup<any>): string[] {
    const errors: string[] = [];
    Object.keys(form.controls).forEach((key) => {
      if (key == "maps_url") {
        return;
      }

      const fieldName = this.getFieldNameByKey(key);
      const controlErrors = form.get(key)?.errors;

      if (controlErrors) {
        Object.keys(controlErrors).forEach((errorKey) => {
          switch (errorKey) {
            case "required":
              if (fieldName == "accepted_terms") {
                errors.push(
                  `Você precisa aceitar os termos e condições para continuar.`,
                );
              } else {
                errors.push(`O campo ${fieldName} é obrigatório.`);
              }
              break;
            case "minlength":
              errors.push(
                `${fieldName} deve ter no mínimo ${controlErrors["minlength"].requiredLength} caracteres.`,
              );
              break;
            case "email":
              errors.push(`${fieldName} deve ser um email válido.`);
              break;
            default:
              errors.push(`Erro desconhecido no campo ${fieldName}.`);
              break;
          }
        });
      }
    });

    return errors;
  }

  getFieldNameByKey(key: string): string {
    switch (key) {
      case "is_private":
        return "visibilidade do evento";
      case "title":
        return "nome do evento";
      case "description":
        return "descrição do evento";
      case "location_name":
        return "nome do local";
      case "zip":
        return "CEP";
      case "city":
        return "cidade";
      case "state":
        return "estado";
      case "street":
        return "rua";
      case "number":
        return "número";
      case "neighborhood":
        return "bairro";
      case "uploadBanner":
        return "imagem do evento";
      default:
        return key;
    }
  }

  getFormValidationErrors(): string[] {
    const form1Errors = this.getFormErrors(this.formGroupStep1);
    const form2Errors = this.getFormErrors(this.formGroupStep2);
    const form3Errors = this.getFormErrors(this.formGroupStep3);

    const errors = [...form1Errors, ...form2Errors, ...form3Errors];

    return errors;
  }

  verify() {
    console.log(
      this.formGroupStep1.valid,
      this.formGroupStep2.valid,
      this.formGroupStep3.valid,
    );
  }
}
